import React, { useState } from 'react';
import {
  Calculator,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  BarChart3,
  Package,
  DollarSign,
  RefreshCw,
  Download,
  Play,
  Pause,
  Settings,
  Activity,
  Target
} from 'lucide-react';
import { mrpData } from '../../data/purchaseCycleData';

const MRPPlanning = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const { mrpCalculations, mrpDashboard } = mrpData;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'urgent': return '緊急';
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '未知';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const renderWorkflowBanner = () => {
    if (!navigationSource) return null;
    
    return (
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calculator className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">採購週期管理 - MRP規劃</h2>
              <p className="text-orange-100 text-sm">Purchase Cycle Management - MRP Planning</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回採購週期</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">計劃訂單</p>
              <p className="text-2xl font-bold text-gray-900">{mrpDashboard.totalPlannedOrders}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">計劃準確率 {mrpDashboard.planningAccuracy}%</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">緊急訂單</p>
              <p className="text-2xl font-bold text-red-600">{mrpDashboard.urgentOrders}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Clock className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600">高優先 {mrpDashboard.highPriorityOrders} 個</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">計劃總值</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(mrpDashboard.totalValue)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <BarChart3 className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-blue-600">平均前置時間 {mrpDashboard.averageLeadTime} 天</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">準時交貨率</p>
              <p className="text-2xl font-bold text-gray-900">{mrpDashboard.onTimeDeliveryRate}%</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <RefreshCw className="w-4 h-4 text-purple-500 mr-1" />
            <span className="text-purple-600">計劃週期 {mrpDashboard.planningCycle}</span>
          </div>
        </div>
      </div>

      {/* Planning Status */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">MRP 計算狀態</h3>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Play className="w-4 h-4" />
              <span>執行計算</span>
            </button>
            <button className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
              <Settings className="w-4 h-4" />
              <span>設定</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-2">最後計算時間</p>
            <p className="text-lg font-medium text-gray-900">{formatDate(mrpDashboard.lastCalculation)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">下次計算時間</p>
            <p className="text-lg font-medium text-blue-600">{formatDate(mrpDashboard.nextCalculation)}</p>
          </div>
        </div>
      </div>

      {/* Priority Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">優先級分布</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{mrpDashboard.urgentOrders}</div>
            <div className="text-sm text-red-600">緊急</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{mrpDashboard.highPriorityOrders}</div>
            <div className="text-sm text-orange-600">高優先</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{mrpDashboard.mediumPriorityOrders}</div>
            <div className="text-sm text-yellow-600">中優先</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{mrpDashboard.lowPriorityOrders}</div>
            <div className="text-sm text-green-600">低優先</div>
          </div>
        </div>
      </div>

      {/* Demand Forecast */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">需求預測</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {mrpDashboard.demandForecast.map((forecast, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{forecast.month}</h4>
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(forecast.demand)}</p>
              <p className="text-sm text-gray-500">預測需求</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveView('calculations')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">查看計算結果</p>
              <p className="text-sm text-gray-500">檢視 MRP 計算詳情</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('recommendations')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Target className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">採購建議</p>
              <p className="text-sm text-gray-500">查看建議的採購行動</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('forecast')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">需求預測</p>
              <p className="text-sm text-gray-500">分析未來需求趨勢</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderCalculations = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">MRP 計算結果</h2>
          <p className="text-gray-600">物料需求計劃計算詳情</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <RefreshCw className="w-4 h-4" />
            <span>重新計算</span>
          </button>
          <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            <Download className="w-4 h-4" />
            <span>匯出</span>
          </button>
        </div>
      </div>

      {/* Calculations List */}
      <div className="bg-white rounded-lg shadow-md">
        {mrpCalculations.map((calculation) => (
          <div key={calculation.id} className="border-b border-gray-200 last:border-b-0">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{calculation.id}</h3>
                  <p className="text-sm text-gray-500">計劃期間: {calculation.planningHorizon}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="inline-flex px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                    {calculation.status === 'completed' ? '已完成' : '進行中'}
                  </span>
                  <button
                    onClick={() => {
                      setSelectedCalculation(calculation);
                      setActiveView('calculation-detail');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">計算時間</p>
                  <p className="font-medium text-gray-900">{formatDate(calculation.calculationDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">項目數量</p>
                  <p className="font-medium text-gray-900">{calculation.totalItems}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">總需求</p>
                  <p className="font-medium text-gray-900">{formatCurrency(calculation.totalRequirement)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">計劃訂單</p>
                  <p className="font-medium text-gray-900">{calculation.totalPlannedOrders}</p>
                </div>
              </div>

              {/* Items Preview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">項目明細 (前3項)</h4>
                <div className="space-y-2">
                  {calculation.items.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-3 rounded">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.itemName}</p>
                        <p className="text-sm text-gray-500">{item.itemCode}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">需求 {item.netRequirement.toLocaleString()}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority)}`}>
                          {getPriorityText(item.priority)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCalculationDetail = () => {
    if (!selectedCalculation) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('calculations')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedCalculation.id}</h2>
              <p className="text-gray-600">{selectedCalculation.planningHorizon}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <CheckCircle className="w-4 h-4" />
              <span>批准計劃</span>
            </button>
          </div>
        </div>

        {/* Calculation Summary */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">計算摘要</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500">計算時間</p>
              <p className="text-lg font-medium text-gray-900">{formatDate(selectedCalculation.calculationDate)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">項目數量</p>
              <p className="text-lg font-medium text-gray-900">{selectedCalculation.totalItems}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">總需求</p>
              <p className="text-lg font-medium text-gray-900">{formatCurrency(selectedCalculation.totalRequirement)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">計劃訂單</p>
              <p className="text-lg font-medium text-gray-900">{selectedCalculation.totalPlannedOrders}</p>
            </div>
          </div>
        </div>

        {/* Items Detail */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">項目明細</h3>
            
            {/* Filters */}
            <div className="mb-4 flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="搜尋項目..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">所有優先級</option>
                <option value="urgent">緊急</option>
                <option value="high">高</option>
                <option value="medium">中</option>
                <option value="low">低</option>
              </select>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">項目</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">當前庫存</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">毛需求</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">淨需求</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">計劃訂單</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">計劃日期</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">前置時間</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">優先級</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議行動</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedCalculation.items.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.itemName}</div>
                        <div className="text-sm text-gray-500">{item.itemCode}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.currentStock.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.grossRequirement.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.netRequirement.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.plannedOrderQty.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(item.plannedOrderDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.leadTime} 天
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority)}`}>
                        {getPriorityText(item.priority)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.suggestedAction === 'place_order' ? '下採購訂單' : item.suggestedAction}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'calculations':
        return renderCalculations();
      case 'calculation-detail':
        return renderCalculationDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      
      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            儀表板
          </button>
          <button
            onClick={() => setActiveView('calculations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'calculations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            計算結果
          </button>
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};

export default MRPPlanning; 